// Client-side data access for the admin "Datos" section.
// Uses the standard Supabase browser client (RLS applies).
// Public SELECT policies exist on these tables so reads work without auth.
// Mutations require authenticated users with the appropriate RLS policies.
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";

const Id = z.object({ id: z.string().uuid() });
const SlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9-]+$/, "Solo minúsculas, números y guiones");

type Call<I, O> = (opts?: { data: I }) => Promise<O>;
type CallNoArgs<O> = () => Promise<O>;

// ───────── Operaciones ─────────
export const listOperaciones: CallNoArgs<any[]> = async () => {
  const { data, error } = await supabase
    .from("operaciones")
    .select("*")
    .order("orden", { ascending: true })
    .order("nombre", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

const OperacionInput = z.object({
  nombre: z.string().min(1).max(80),
  slug: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9-]+$/, "Solo minúsculas, números y guiones"),
  orden: z.number().int().min(0).max(9999),
  activo: z.boolean(),
});

export const createOperacion: Call<z.infer<typeof OperacionInput>, { ok: true }> = async (opts) => {
  const data = OperacionInput.parse(opts?.data);
  const { error } = await supabase.from("operaciones").insert(data);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const updateOperacion: Call<z.infer<typeof OperacionInput> & { id: string }, { ok: true }> = async (opts) => {
  const parsed = OperacionInput.extend(Id.shape).parse(opts?.data);
  const { id, ...rest } = parsed;
  const { error } = await supabase.from("operaciones").update(rest).eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const deleteOperacion: Call<{ id: string }, { ok: true }> = async (opts) => {
  const { id } = Id.parse(opts?.data);
  const { error } = await supabase.from("operaciones").delete().eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

// ───────── Tipos de local (N:N con operaciones) ─────────
export const listTiposLocal: Call<{ operacionId?: string } | undefined, any[]> = async (opts) => {
  const data = z
    .object({ operacionId: z.string().uuid().optional() })
    .parse(opts?.data ?? {});
  let tipoIds: string[] | null = null;
  if (data.operacionId) {
    const { data: links, error: e1 } = await supabase
      .from("tipo_local_operaciones")
      .select("tipo_local_id")
      .eq("operacion_id", data.operacionId);
    if (e1) throw new Error(e1.message);
    tipoIds = (links ?? []).map((l: { tipo_local_id: string }) => l.tipo_local_id);
    if (tipoIds.length === 0) return [];
  }
  let q = supabase
    .from("tipos_local")
    .select("*, tipo_local_operaciones(operaciones(id, nombre, slug))")
    .order("orden", { ascending: true })
    .order("nombre", { ascending: true });
  if (tipoIds) q = q.in("id", tipoIds);
  const { data: rows, error } = await q;
  if (error) throw new Error(error.message);
  return rows ?? [];
};

const TipoInput = z.object({
  operacion_ids: z.array(z.string().uuid()).min(1),
  nombre: z.string().min(1).max(80),
  slug: SlugSchema,
  orden: z.number().int().min(0).max(9999),
  activo: z.boolean(),
});

async function syncTipoOperaciones(tipoId: string, operacionIds: string[]) {
  const { error: delErr } = await supabase
    .from("tipo_local_operaciones")
    .delete()
    .eq("tipo_local_id", tipoId);
  if (delErr) throw new Error(delErr.message);
  if (operacionIds.length === 0) return;
  const rows = operacionIds.map((operacion_id) => ({ tipo_local_id: tipoId, operacion_id }));
  const { error: insErr } = await supabase.from("tipo_local_operaciones").insert(rows);
  if (insErr) throw new Error(insErr.message);
}

export const createTipoLocal: Call<z.infer<typeof TipoInput>, { ok: true }> = async (opts) => {
  const parsed = TipoInput.parse(opts?.data);
  const { operacion_ids, ...rest } = parsed;
  const { data: row, error } = await supabase
    .from("tipos_local")
    .insert(rest)
    .select("id")
    .single();
  if (error) throw new Error(error.message);
  await syncTipoOperaciones(row.id, operacion_ids);
  return { ok: true };
};

export const updateTipoLocal: Call<z.infer<typeof TipoInput> & { id: string }, { ok: true }> = async (opts) => {
  const parsed = TipoInput.extend(Id.shape).parse(opts?.data);
  const { id, operacion_ids, ...rest } = parsed;
  const { error } = await supabase.from("tipos_local").update(rest).eq("id", id);
  if (error) throw new Error(error.message);
  await syncTipoOperaciones(id, operacion_ids);
  return { ok: true };
};

export const deleteTipoLocal: Call<{ id: string }, { ok: true }> = async (opts) => {
  const { id } = Id.parse(opts?.data);
  const { error } = await supabase.from("tipos_local").delete().eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

// ───────── Usos de local ─────────
export const listUsosLocal: Call<{ tipoLocalId?: string } | undefined, any[]> = async (opts) => {
  const data = z
    .object({ tipoLocalId: z.string().uuid().optional() })
    .parse(opts?.data ?? {});
  let q = supabase
    .from("usos_local")
    .select("*, tipos_local(id, nombre)")
    .order("tipos_local(nombre)", { ascending: true })
    .order("orden", { ascending: true })
    .order("nombre", { ascending: true });
  if (data.tipoLocalId) q = q.eq("tipo_local_id", data.tipoLocalId);
  const { data: rows, error } = await q;
  if (error) throw new Error(error.message);
  return rows ?? [];
};

const UsoInput = z.object({
  tipo_local_id: z.string().uuid(),
  nombre: z.string().min(1).max(80),
  slug: SlugSchema,
  orden: z.number().int().min(0).max(9999),
  activo: z.boolean(),
});

export const createUsoLocal: Call<z.infer<typeof UsoInput>, { ok: true }> = async (opts) => {
  const data = UsoInput.parse(opts?.data);
  const { error } = await supabase.from("usos_local").insert(data);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const updateUsoLocal: Call<z.infer<typeof UsoInput> & { id: string }, { ok: true }> = async (opts) => {
  const parsed = UsoInput.extend(Id.shape).parse(opts?.data);
  const { id, ...rest } = parsed;
  const { error } = await supabase.from("usos_local").update(rest).eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const deleteUsoLocal: Call<{ id: string }, { ok: true }> = async (opts) => {
  const { id } = Id.parse(opts?.data);
  const { error } = await supabase.from("usos_local").delete().eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

// ───────── Licencias (siempre ligadas a 'traspaso') ─────────
async function getTraspasoId(): Promise<string | null> {
  const { data, error } = await supabase
    .from("operaciones")
    .select("id")
    .eq("slug", "traspaso")
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data?.id ?? null;
}

export const listLicencias: CallNoArgs<{ traspasoId: string | null; items: any[] }> = async () => {
  const traspasoId = await getTraspasoId();
  if (!traspasoId) return { traspasoId: null, items: [] };
  const { data, error } = await supabase
    .from("licencias")
    .select("*")
    .eq("operacion_id", traspasoId)
    .order("orden", { ascending: true })
    .order("nombre", { ascending: true });
  if (error) throw new Error(error.message);
  return { traspasoId, items: data ?? [] };
};

const LicenciaInput = z.object({
  nombre: z.string().min(1).max(80),
  slug: SlugSchema,
  orden: z.number().int().min(0).max(9999),
  activo: z.boolean(),
});

export const createLicencia: Call<z.infer<typeof LicenciaInput>, { ok: true }> = async (opts) => {
  const data = LicenciaInput.parse(opts?.data);
  const traspasoId = await getTraspasoId();
  if (!traspasoId) throw new Error("Falta la operación 'traspaso' en operaciones.");
  const { error } = await supabase.from("licencias").insert({ ...data, operacion_id: traspasoId });
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const updateLicencia: Call<z.infer<typeof LicenciaInput> & { id: string }, { ok: true }> = async (opts) => {
  const parsed = LicenciaInput.extend(Id.shape).parse(opts?.data);
  const { id, ...rest } = parsed;
  const { error } = await supabase.from("licencias").update(rest).eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const deleteLicencia: Call<{ id: string }, { ok: true }> = async (opts) => {
  const { id } = Id.parse(opts?.data);
  const { error } = await supabase.from("licencias").delete().eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

// ───────── Importadores ─────────
export const PLATAFORMAS = ["kyero", "idealista", "fotocasa", "bubble", "alfainmo"] as const;
export const ESTADOS_IMPORTADOR = ["activo", "inactivo"] as const;
export const FRECUENCIAS = ["1m","5m","15m","30m","1h","2h","6h","12h","24h","72h"] as const;

const ImportadorInput = z.object({
  url: z.string().max(2000),
  plataforma: z.enum(PLATAFORMAS),
  estado: z.enum(ESTADOS_IMPORTADOR),
  frecuencia: z.enum(FRECUENCIAS),
  provincia_id: z.string().uuid().nullable(),
  /** Solo kyero: agencia dueña del feed (el XML no la identifica). */
  agencia_id: z.string().uuid().nullable().optional(),
});

export const listImportadores: CallNoArgs<any[]> = async () => {
  const { data, error } = await supabase
    .from("importadores")
    .select("*, provincias(id, nombre), agencias(id, nombre)")
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const listProvincias: CallNoArgs<any[]> = async () => {
  const { data, error } = await supabase
    .from("provincias")
    .select("id, nombre")
    .order("nombre", { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
};

export const createImportador: Call<z.infer<typeof ImportadorInput>, { ok: true }> = async (opts) => {
  const data = ImportadorInput.parse(opts?.data);
  const { error } = await supabase.from("importadores").insert(data);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const updateImportador: Call<Partial<z.infer<typeof ImportadorInput>> & { id: string }, { ok: true }> = async (opts) => {
  const parsed = z.object({ id: z.string().uuid() }).and(ImportadorInput.partial()).parse(opts?.data);
  const { id, ...rest } = parsed;
  const { error } = await supabase.from("importadores").update(rest).eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const deleteImportador: Call<{ id: string }, { ok: true }> = async (opts) => {
  const { id } = Id.parse(opts?.data);
  const { error } = await supabase.from("importadores").delete().eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

// ───────── Importaciones (tracking) ─────────
const ImportacionesQuery = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(200).default(50),
  plataforma: z.string().optional(),
  estado: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  /** Búsqueda libre por ID externo, URL de origen o mensaje de error. */
  q: z.string().trim().max(200).optional(),
});

export const listImportaciones: Call<
  Partial<z.infer<typeof ImportacionesQuery>> | undefined,
  { items: any[]; total: number; page: number; pageSize: number }
> = async (opts) => {
  const { page, pageSize, plataforma, estado, from, to, q: search } = ImportacionesQuery.parse(opts?.data ?? {});
  let q = supabase
    .from("importaciones")
    .select("*, importadores(url, plataforma)", { count: "exact" })
    .order("updated_at", { ascending: false });
  if (plataforma) q = q.eq("plataforma", plataforma);
  // "revision" = filas en error esperando una decisión humana (cola alfainmo).
  if (estado === "revision") q = q.eq("estado_global", "error").like("error", "revision\\_%");
  else if (estado) q = q.eq("estado_global", estado as any);
  if (search) {
    // Quita los caracteres con significado en la sintaxis de .or() de PostgREST.
    const term = search.replace(/[,()*%\\]/g, " ").trim();
    if (term) {
      q = q.or(`external_id.ilike.*${term}*,url_origen.ilike.*${term}*,error.ilike.*${term}*`);
    }
  }
  if (from) q = q.gte("updated_at", new Date(from).toISOString());
  if (to) q = q.lte("updated_at", new Date(to).toISOString());
  const fromIdx = (page - 1) * pageSize;
  const toIdx = fromIdx + pageSize - 1;
  q = q.range(fromIdx, toIdx);
  const { data, error, count } = await q;
  if (error) throw new Error(error.message);
  return { items: data ?? [], total: count ?? 0, page, pageSize };
};

// ───────── Tipos de documento ─────────
export type DocumentoTipoRow = {
  id: string;
  nombre: string;
  slug: string;
  orden: number;
  activo: boolean;
  opcional: boolean;
  en_contactos: boolean;
  en_agencias: boolean;
  en_anuncios: boolean;
  en_operaciones: boolean;
  operacion_ids: string[];
  guarda_en: string | null;
};

export type DocumentoScope = "contacto" | "agencia" | "anuncio" | "transaccion";

const SCOPE_COL: Record<DocumentoScope, "en_contactos" | "en_agencias" | "en_anuncios" | "en_operaciones"> = {
  contacto: "en_contactos",
  agencia: "en_agencias",
  anuncio: "en_anuncios",
  transaccion: "en_operaciones",
};

// Resuelve la operación del anuncio (directo o vía transacción) para filtrar tipos.
async function resolveOperacionId(args: { anuncioId?: string; transaccionId?: string }): Promise<string | null> {
  let anuncioId = args.anuncioId ?? null;
  if (!anuncioId && args.transaccionId) {
    const { data } = await supabase
      .from("transacciones")
      .select("anuncio_id")
      .eq("id", args.transaccionId)
      .maybeSingle();
    anuncioId = (data as any)?.anuncio_id ?? null;
  }
  if (!anuncioId) return null;
  const { data } = await supabase.from("anuncios").select("operacion_id").eq("id", anuncioId).maybeSingle();
  return (data as any)?.operacion_id ?? null;
}

export const listDocumentoTipos: Call<
  { scope?: DocumentoScope; soloActivos?: boolean; anuncioId?: string; transaccionId?: string } | undefined,
  DocumentoTipoRow[]
> = async (opts) => {
  const scope = opts?.data?.scope;
  const soloActivos = opts?.data?.soloActivos ?? false;
  let q = supabase
    .from("documento_tipos")
    .select(
      "id, nombre, slug, orden, activo, opcional, en_contactos, en_agencias, en_anuncios, en_operaciones, operacion_ids, guarda_en",
    )
    .order("orden", { ascending: true })
    .order("nombre", { ascending: true });
  if (soloActivos) q = q.eq("activo", true);
  if (scope) q = q.eq(SCOPE_COL[scope], true);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  let rows = (data ?? []).map((r: any) => ({ ...r, operacion_ids: r.operacion_ids ?? [] })) as DocumentoTipoRow[];

  if (opts?.data?.anuncioId || opts?.data?.transaccionId) {
    const operacionId = await resolveOperacionId({
      anuncioId: opts?.data?.anuncioId,
      transaccionId: opts?.data?.transaccionId,
    });
    rows = rows.filter(
      (t) => t.operacion_ids.length === 0 || (operacionId ? t.operacion_ids.includes(operacionId) : false),
    );
  }
  return rows;
};

const DocumentoTipoInput = z.object({
  nombre: z.string().min(1).max(80),
  slug: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9_]+$/, "Solo minúsculas, números y guiones bajos"),
  orden: z.number().int().min(0).max(9999),
  activo: z.boolean(),
  opcional: z.boolean(),
  en_contactos: z.boolean(),
  en_agencias: z.boolean(),
  en_anuncios: z.boolean(),
  en_operaciones: z.boolean(),
  operacion_ids: z.array(z.string().uuid()).default([]),
  guarda_en: z
    .enum(["contacto", "agencia", "anuncio", "operacion"])
    .nullable()
    .default(null),
});


export const createDocumentoTipo: Call<z.infer<typeof DocumentoTipoInput>, { ok: true }> = async (opts) => {
  const data = DocumentoTipoInput.parse(opts?.data);
  const { error } = await supabase.from("documento_tipos").insert(data);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const updateDocumentoTipo: Call<z.infer<typeof DocumentoTipoInput> & { id: string }, { ok: true }> = async (
  opts,
) => {
  const parsed = DocumentoTipoInput.extend(Id.shape).parse(opts?.data);
  const { id, ...rest } = parsed;
  const { error } = await supabase.from("documento_tipos").update(rest).eq("id", id);
  if (error) throw new Error(error.message);
  return { ok: true };
};

export const deleteDocumentoTipo: Call<{ id: string }, { ok: true }> = async (opts) => {
  const { id } = Id.parse(opts?.data);
  const { error } = await supabase.from("documento_tipos").delete().eq("id", id);
  if (error) {
    if ((error as any).code === "23503") {
      throw new Error("No se puede eliminar: hay documentos con este tipo. Desactívalo en su lugar.");
    }
    throw new Error(error.message);
  }
  return { ok: true };
};
